import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchQueuedNotifications, cancelQueuedNotification, retryFailedNotification } from '../notificationSlice';

const NotificationQueuePanel = () => {
  const dispatch = useDispatch();
  const { queue, loading, error } = useSelector(state => state.notification);
  const [filters, setFilters] = useState({
    status: '',
    trigger_type: '',
    recipient_type: ''
  });

  useEffect(() => {
    dispatch(fetchQueuedNotifications(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleCancel = async (id) => {
    if (!window.confirm('이 알림을 취소하시겠습니까?')) return;
    try {
      await dispatch(cancelQueuedNotification(id)).unwrap();
      window.alert('알림이 취소되었습니다.');
      dispatch(fetchQueuedNotifications(filters));
    } catch (error) {
      window.alert(`취소 실패: ${error.error || error.message || '알 수 없는 오류'}`);
    }
  };

  const handleRetry = async (id) => {
    if (!window.confirm('이 알림을 다시 발송하시겠습니까?')) return;
    try {
      await dispatch(retryFailedNotification(id)).unwrap();
      window.alert('알림이 성공적으로 재발송되었습니다.');
      dispatch(fetchQueuedNotifications(filters));
    } catch (error) {
      window.alert(`재발송 실패: ${error.error || error.message || '알 수 없는 오류'}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, statusDisplay) => {
    const statusClasses = {
      queued: 'status-queued',
      sent: 'status-sent',
      failed: 'status-failed'
    };

    return (
      <span className={`status-badge ${statusClasses[status] || ''}`}>
        {statusDisplay}
      </span>
    );
  };

  const getTriggerBadge = (triggerType, triggerDisplay) => {
    const triggerClasses = {
      assignment_created: 'trigger-created',
      dday_3_notification: 'trigger-dday',
      dday_1_notification: 'trigger-dday',
      completion_notification: 'trigger-completion',
      overdue_notification: 'trigger-overdue',
      class_notice: 'trigger-paper',
      homework_assigned: 'trigger-paper',
      grading_completed: 'trigger-paper'
    };

    return (
      <span className={`trigger-badge ${triggerClasses[triggerType] || ''}`}>
        {triggerDisplay}
      </span>
    );
  };

  const getRecipientBadge = (recipientType, recipientDisplay) => {
    const recipientClasses = {
      student: 'recipient-student',
      parent: 'recipient-parent'
    };

    return (
      <span className={`recipient-badge ${recipientClasses[recipientType] || ''}`}>
        {recipientDisplay}
      </span>
    );
  };

  return (
    <div className="queue-panel">
      <div className="panel-header">
        <div className="header-content">
          <h3>알림 대기열</h3>
          <p>오후 11시 이후 대기 중인 알림을 확인하고 관리할 수 있습니다. 다음 날 오후 6시에 자동으로 발송됩니다.</p>
        </div>
        <div className="header-actions">
          <button
            className="refresh-button"
            onClick={() => dispatch(fetchQueuedNotifications(filters))}
            disabled={loading.queue}
          >
            {loading.queue ? '새로고침 중...' : '새로고침'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>상태</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">전체</option>
              <option value="queued">대기중</option>
              <option value="sent">발송완료</option>
              <option value="failed">발송실패</option>
            </select>
          </div>

          <div className="filter-group">
            <label>트리거</label>
            <select
              value={filters.trigger_type}
              onChange={(e) => handleFilterChange('trigger_type', e.target.value)}
              className="filter-select"
            >
              <option value="">전체</option>
              <option value="assignment_created">과제배정</option>
              <option value="dday_3_notification">D-3 알림</option>
              <option value="dday_1_notification">D-1 알림</option>
              <option value="completion_notification">과제완료</option>
              <option value="overdue_notification">만료 알림</option>
              <option value="class_notice">수업 알림장</option>
              <option value="homework_assigned">지필과제 배정</option>
              <option value="grading_completed">채점 완료</option>
            </select>
          </div>

          <div className="filter-group">
            <label>수신자</label>
            <select
              value={filters.recipient_type}
              onChange={(e) => handleFilterChange('recipient_type', e.target.value)}
              className="filter-select"
            >
              <option value="">전체</option>
              <option value="student">학생</option>
              <option value="parent">학부모</option>
            </select>
          </div>
        </div>
      </div>

      {/* Queue Table */}
      <div className="queue-content">
        {loading.queue ? (
          <div className="loading-message">
            대기열을 불러오는 중입니다...
          </div>
        ) : queue.length === 0 ? (
          <div className="empty-message">
            <div className="empty-icon">📭</div>
            <p>대기 중인 알림이 없습니다.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="queue-table">
              <thead>
                <tr>
                  <th>생성일시</th>
                  <th>학생</th>
                  <th>수신자</th>
                  <th>트리거</th>
                  <th>상태</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.created_at)}</td>
                    <td className="student-cell">
                      <div className="student-info">
                        <div className="student-name">{item.student_name}</div>
                        <div className="recipient-phone">{item.recipient_phone}</div>
                      </div>
                    </td>
                    <td>{getRecipientBadge(item.recipient_type, item.recipient_type_display)}</td>
                    <td>{getTriggerBadge(item.trigger_type, item.trigger_type_display)}</td>
                    <td>{getStatusBadge(item.status, item.status_display)}</td>
                    <td className="actions-cell">
                      {item.status === 'queued' && (
                        <button
                          className="action-button cancel-button"
                          onClick={() => handleCancel(item.id)}
                          title="취소"
                        >
                          취소
                        </button>
                      )}
                      {item.status === 'failed' && (
                        <button
                          className="action-button retry-button"
                          onClick={() => handleRetry(item.id)}
                          title="재시도"
                        >
                          재시도
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {error.queue && (
          <div className="error-message">
            {error.queue}
          </div>
        )}
      </div>

      <style jsx>{`
        .queue-panel {
          background: #fff;
          border-radius: 8px;
          padding: 24px;
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #E6EDEC;
        }

        .header-content h3 {
          margin: 0 0 8px 0;
          font-size: 20px;
          font-weight: 600;
          color: #111827;
        }

        .header-content p {
          margin: 0;
          font-size: 14px;
          color: #6b7280;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .refresh-button {
          padding: 10px 20px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }

        .refresh-button:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .refresh-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .filters-section {
          background: #F4F8F7;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .filter-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .filter-group {
          flex: 1;
          min-width: 200px;
        }

        .filter-group label {
          display: block;
          margin-bottom: 6px;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
        }

        .filter-select {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        .queue-content {
          margin-top: 20px;
        }

        .loading-message,
        .empty-message {
          text-align: center;
          padding: 60px 20px;
          color: #6b7280;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 12px;
        }

        .empty-message p {
          margin: 0;
          font-size: 16px;
        }

        .table-container {
          overflow-x: auto;
          border-radius: 8px;
          border: 1px solid #E6EDEC;
        }

        .queue-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .queue-table thead {
          background: #f9fafb;
        }

        .queue-table th {
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          color: #111827;
          border-bottom: 2px solid #E6EDEC;
        }

        .queue-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f3f4f6;
        }

        .queue-table tr:last-child td {
          border-bottom: none;
        }

        .queue-table tbody tr:hover {
          background: #f9fafb;
        }

        .student-cell {
          min-width: 180px;
        }

        .student-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .student-name {
          font-weight: 500;
          color: #111827;
        }

        .recipient-phone {
          font-size: 12px;
          color: #6b7280;
        }

        .scheduled-time {
          display: inline-block;
          padding: 4px 8px;
          background: #fef3c7;
          color: #92400e;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-queued {
          background: #FBF4D9;
          color: #8A6D00;
        }

        .status-sent {
          background: #E5FBF8;
          color: #00806B;
        }

        .status-failed {
          background: #FCEDE9;
          color: #A93A25;
        }

        .trigger-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .trigger-created {
          background: #E5FBF8;
          color: #1565c0;
        }

        .trigger-dday {
          background: #fff3e0;
          color: #e65100;
        }

        .trigger-completion {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .trigger-overdue {
          background: #FCEDE9;
          color: #c2185b;
        }

        .trigger-paper {
          background: #f3e5f5;
          color: #6a1b9a;
        }

        .recipient-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
        }

        .recipient-student {
          background: #E5FBF8;
          color: #1565c0;
        }

        .recipient-parent {
          background: #FCEDE9;
          color: #c2185b;
        }

        .actions-cell {
          white-space: nowrap;
        }

        .action-button {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .cancel-button {
          background: #fee;
          color: #c00;
        }

        .cancel-button:hover {
          background: #fcc;
        }

        .retry-button {
          background: #E5FBF8;
          color: #1565c0;
        }

        .retry-button:hover {
          background: #bbdefb;
        }

        .error-message {
          margin-top: 12px;
          padding: 12px;
          background: #fee;
          border: 1px solid #fcc;
          border-radius: 4px;
          color: #c00;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .queue-panel {
            padding: 16px;
          }

          .panel-header {
            flex-direction: column;
            gap: 16px;
          }

          .header-actions {
            width: 100%;
          }

          .header-actions button {
            flex: 1;
          }

          .filter-row {
            flex-direction: column;
          }

          .filter-group {
            min-width: 100%;
          }

          .table-container {
            overflow-x: scroll;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationQueuePanel;
